import { useEffect, useState } from "react";
import axios from "axios";
import { Bell, X, Check, ArrowUpRight, ChevronRight, FileText, Plus, Upload, MessageCircle, Camera } from "lucide-react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const imgHero = "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1800&q=85";
export const imgKumaon = "https://images.unsplash.com/photo-1500534623283-312aade485b7?auto=format&fit=crop&w=900&q=80";
// Premium rotating hero visuals — Kumaoni hills, contemporary Indian, coastal, urban.
export const HERO_IMAGES = [
  { src: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1800&q=85", label: "Kumaoni hill home" },
  { src: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1800&q=85", label: "Contemporary Indian home" },
  { src: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=1800&q=85", label: "Modern villa" },
  { src: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1800&q=85", label: "Urban construction" },
];
// Rotates every hour so a repeat visit within the same session sees the same shot
// (avoids flicker on view switches) but a fresh visit later feels alive.
export function pickHero(seed) {
  const hourBucket = Math.floor(Date.now() / (1000 * 60 * 60));
  const s = (seed || "") + "";
  let hash = hourBucket;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0;
  return HERO_IMAGES[Math.abs(hash) % HERO_IMAGES.length];
}
export const KUMAON_CITIES = ["Haldwani", "Nainital", "Bhimtal", "Bhowali", "Mukteshwar", "Almora", "Ranikhet", "Binsar", "Pithoragarh", "Munsyari", "Chaukori"];
export const MATERIAL_CATEGORIES = ["Cement", "Steel", "Bricks & Blocks", "Sand & Aggregates", "Tiles", "Stone", "Paint", "Electrical", "Plumbing", "Sanitaryware", "Doors & Windows", "Hardware", "Roofing", "Waterproofing", "Kitchen", "Bathroom", "Lighting", "Plywood", "Timber"];
export const PROJECT_STAGES = ["Planning", "Design", "Quotation", "Selection", "Construction", "Finishing", "Completed"];

export const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
export const shortDate = (s) => s ? new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—";
export const chatTime = (s) => s ? new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "";
export const errorText = (e) => typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Something went wrong. Please try again.";
export const fileToDataURL = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

export const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => { const t = localStorage.getItem("kumaon_token"); if (t) config.headers.Authorization = `Bearer ${t}`; return config; });

// Approved DAZYU brand mark — green D-shaped emblem with white pitched-roof house
// and a 2×2 pane window inside. Exported as separate `LogoMark` (icon only) so it
// can also be used as favicon / app icon, and `Logo` (mark + wordmark).
export function LogoMark({ size = 36, className = "" }) {
  return <span className={`logo-mark ${className}`} aria-hidden="true" style={{ width: size, height: size }}>
    <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" width={size} height={size}>
      {/* Green D emblem — flat left edge, organic leaf-like curve on the right */}
      <path d="M10 8 H45 C 78 8 92 28 92 50 C 92 74 76 92 45 92 H10 Z" fill="#135943"/>
      {/* White house body */}
      <rect x="30" y="50" width="40" height="28" rx="1.5" fill="#ffffff"/>
      {/* White pitched roof triangle sitting on the body */}
      <path d="M25 52 L50 32 L75 52 Z" fill="#ffffff"/>
      {/* Four-pane window — green frame + cross mullions on a white square */}
      <rect x="42" y="58" width="16" height="14" fill="#135943"/>
      <rect x="43.2" y="59.2" width="6.6" height="5.4" fill="#ffffff"/>
      <rect x="50.2" y="59.2" width="6.6" height="5.4" fill="#ffffff"/>
      <rect x="43.2" y="65.4" width="6.6" height="5.4" fill="#ffffff"/>
      <rect x="50.2" y="65.4" width="6.6" height="5.4" fill="#ffffff"/>
    </svg>
  </span>;
}

export function Logo() { return <div className="logo" data-testid="brand-logo">
  <LogoMark size={36}/>
  <span className="logo-word">DAZYU<sup>®</sup></span>
</div>; }

export function PageHeader({ eyebrow, title, sub, children }) {
  return <header className="page-header"><div><span className="eyebrow">{eyebrow}</span><h1 data-testid="page-title">{title}</h1>{sub && <p>{sub}</p>}</div>{children}</header>;
}

export function Stat({ label, value, note, tone = "green", testId }) {
  return <div className={`stat ${tone}`} data-testid={testId || `stat-${label.toLowerCase().replaceAll(" ", "-")}`}><span>{label}</span><strong>{value}</strong><small>{note}</small></div>;
}

export function Empty({ title, text, action, onClick }) {
  return <div className="empty"><div className="empty-icon"><FileText size={22} /></div><h3>{title}</h3><p>{text}</p>{action && <button className="button primary" onClick={onClick} data-testid="empty-action">{action}<ArrowUpRight size={15} /></button>}</div>;
}

export function StatusPill({ status }) {
  const good = ["Accepted", "Confirmed", "Completed", "Verified", "Delivered"];
  const warn = ["Pending", "Sent", "Viewed", "Received"];
  const bad = ["Rejected", "Cancelled", "Not Selected", "Suspended", "Expired"];
  const cls = good.includes(status) ? "success" : bad.includes(status) ? "danger" : warn.includes(status) ? "warn" : "";
  return <span className={`status-pill ${cls}`}>{status || "—"}</span>;
}

export function Stars({ value = 0, size = 12 }) {
  return <span className="stars" aria-label={`${value} out of 5`}>{[1,2,3,4,5].map(i => <span key={i} className={i <= Math.round(value) ? "on" : ""} style={{fontSize:size+2}}>★</span>)}</span>;
}

export function Modal({ children, onClose, title, eyebrow }) {
  return <div className="modal-backdrop" onClick={onClose}><div className="modal" onClick={e => e.stopPropagation()}><button className="modal-close" onClick={onClose} data-testid="modal-close"><X /></button>{eyebrow && <span className="eyebrow">{eyebrow}</span>}{title && <h2>{title}</h2>}{children}</div></div>;
}

// Singleton SSE stream shared across NotificationsBell mounts.
// A short-lived stream token is fetched from /api/auth/stream-token and refreshed before expiry.
let _sseRef = { es: null, refs: 0, subscribers: new Set(), refreshTimer: null };
async function _openStream() {
  try {
    const t = await api.post("/auth/stream-token");
    const streamToken = t.data.token; const expiresIn = t.data.expires_in || 300;
    if (_sseRef.es) { try { _sseRef.es.close(); } catch {} }
    _sseRef.es = new EventSource(`${API}/notifications/stream?token=${encodeURIComponent(streamToken)}`);
    _sseRef.es.addEventListener("notification", (ev) => {
      try { const n = JSON.parse(ev.data); _sseRef.subscribers.forEach(fn => fn(n)); } catch {}
    });
    // Refresh before the token expires (60s early)
    if (_sseRef.refreshTimer) clearTimeout(_sseRef.refreshTimer);
    _sseRef.refreshTimer = setTimeout(() => { if (_sseRef.refs > 0) _openStream(); }, Math.max(30, expiresIn - 60) * 1000);
  } catch {
    _sseRef.es = null;
  }
}
function subscribeStream(cb) {
  _sseRef.subscribers.add(cb);
  _sseRef.refs += 1;
  if (!_sseRef.es && localStorage.getItem("kumaon_token")) _openStream();
  return () => {
    _sseRef.subscribers.delete(cb); _sseRef.refs -= 1;
    if (_sseRef.refs <= 0) {
      if (_sseRef.es) { try { _sseRef.es.close(); } catch {} }
      if (_sseRef.refreshTimer) clearTimeout(_sseRef.refreshTimer);
      _sseRef = { es: null, refs: 0, subscribers: new Set(), refreshTimer: null };
    }
  };
}

export function NotificationsBell() {
  const [items, setItems] = useState([]); const [open, setOpen] = useState(false); const [toasts, setToasts] = useState([]);
  const load = () => api.get("/notifications").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const unsub = subscribeStream((n) => {
      setItems(prev => prev.some(x => x.id === n.id) ? prev : [n, ...prev]);
      const t = { id: n.id + "_t_" + Math.random().toString(36).slice(2, 6), message: n.message };
      setToasts(prev => [...prev, t]);
      setTimeout(() => setToasts(prev => prev.filter(x => x.id !== t.id)), 5000);
    });
    return unsub;
  }, []);
  const unread = items.filter(i => !i.read).length;
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };
  const readOne = async (id) => { await api.patch(`/notifications/${id}/read`); load(); };
  return <>
    <div className="notif-wrap"><button className="notif-btn" onClick={() => setOpen(!open)} data-testid="notif-bell"><Bell size={17} />{unread > 0 && <span className="notif-dot" data-testid="notif-count">{unread}</span>}</button>{open && <div className="notif-panel" data-testid="notif-panel"><div className="notif-head"><b>Notifications</b>{unread > 0 && <button onClick={readAll} data-testid="notif-read-all">Mark all read</button>}</div>{items.length === 0 ? <div className="notif-empty">You're all caught up.</div> : items.slice(0, 12).map(n => <div key={n.id} className={`notif-item ${n.read ? "" : "unread"}`} onClick={() => readOne(n.id)} data-testid={`notif-${n.id}`}><div><b>{n.message}</b><small>{shortDate(n.created_at)}</small></div>{!n.read && <Check size={13} />}</div>)}</div>}</div>
    {toasts.length > 0 && <div className="toast-stack" data-testid="toast-stack">{toasts.map(t => <div className="toast" key={t.id} data-testid={`toast-${t.id}`}><Bell size={16}/><div><b>New update</b><span>{t.message}</span></div></div>)}</div>}
  </>;
}

export function ReviewModal({ booking, onClose, onDone }) {
  const [rating, setRating] = useState(5); const [text, setText] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async () => { setBusy(true); try { await api.post("/reviews", { booking_id: booking.id, rating, review: text }); onDone && onDone(); onClose(); } catch (e) { alert(errorText(e)); } finally { setBusy(false); } };
  return <Modal onClose={onClose} eyebrow="SHARE YOUR EXPERIENCE" title={`Rate ${booking.provider_name}`}>
    <p className="muted small">Your review helps other homeowners choose with confidence.</p>
    <div className="rating-picker" data-testid="rating-picker">{[1,2,3,4,5].map(i => <button key={i} className={i <= rating ? "on" : ""} onClick={() => setRating(i)} data-testid={`star-${i}`}>★</button>)}</div>
    <label>Your review<textarea data-testid="review-text" value={text} onChange={e => setText(e.target.value)} placeholder="What worked well? Would you recommend them?" /></label>
    <button className="button primary full" onClick={submit} disabled={busy} data-testid="submit-review">{busy ? "Sending…" : "Publish review"} <ArrowUpRight size={16} /></button>
  </Modal>;
}

export function ProjectSelect({ projects, value, onChange, testId = "project-select", label = "Project" }) {
  return <label>{label}<select data-testid={testId} value={value} onChange={e => onChange(e.target.value)}><option value="">Select a project</option>{projects?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>;
}

export function BookingDetail({ booking, user, onClose, onChanged }) {
  const [tab, setTab] = useState("chat");
  const [messages, setMessages] = useState([]);
  const [progress, setProgress] = useState([]);
  const [msg, setMsg] = useState("");
  const [pForm, setPForm] = useState({ image: "", caption: "" });
  const [busy, setBusy] = useState(false);
  const isProvider = user.role === "professional" || user.role === "supplier";
  const isParticipant = user.id === booking.customer_id || user.id === booking.provider_user_id || user.role === "admin";
  const load = () => {
    if (!isParticipant) return;
    api.get(`/bookings/${booking.id}/messages`).then(r => setMessages(r.data)).catch(() => {});
    api.get(`/bookings/${booking.id}/progress`).then(r => setProgress(r.data)).catch(() => {});
  };
  useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, [booking.id]);
  const send = async () => { const text = msg.trim(); if (!text) return; setMsg(""); try { await api.post(`/bookings/${booking.id}/messages`, { message: text }); load(); onChanged && onChanged(); } catch (e) { alert(errorText(e)); setMsg(text); } };
  const handleFile = async (file) => { if (!file) return; if (file.size > 10 * 1024 * 1024) return alert("Files must be 10MB or smaller"); const dataUrl = await fileToDataURL(file); setPForm(p => ({ ...p, image: dataUrl })); };
  const postProgress = async () => { if (!pForm.image) return alert("Add a photo first."); setBusy(true); try { await api.post(`/bookings/${booking.id}/progress`, pForm); setPForm({ image: "", caption: "" }); load(); onChanged && onChanged(); } catch (e) { alert(errorText(e)); } finally { setBusy(false); } };
  return <Modal onClose={onClose} eyebrow={`BOOKING · ${booking.status}`} title={booking.service}>
    <div className="booking-modal-head"><span>{booking.provider_name} · {booking.project_name}</span><b>{fmt(booking.amount)}</b></div>
    <div className="tabs modal-tabs" role="tablist">
      <button className={tab === "chat" ? "on" : ""} onClick={() => setTab("chat")} data-testid="booking-tab-chat"><MessageCircle size={13}/> Chat</button>
      <button className={tab === "progress" ? "on" : ""} onClick={() => setTab("progress")} data-testid="booking-tab-progress"><Camera size={13}/> Progress</button>
    </div>
    {tab === "chat" && <div className="chat">
      <div className="chat-list" data-testid="chat-list">{messages.length === 0 ? <p className="muted small chat-empty">No messages yet. Discuss scope, share links, plan a site visit.</p> : messages.map(m => <div key={m.id} className={`chat-msg ${m.sender_id === user.id ? "mine" : ""}`} data-testid={`msg-${m.id}`}><span className="chat-sender">{m.sender_id === user.id ? "You" : m.sender_name}</span><p>{m.message}</p><small>{chatTime(m.created_at)}</small></div>)}</div>
      <div className="chat-input"><input value={msg} onChange={e => setMsg(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); send(); } }} placeholder="Type a message…" data-testid="chat-input"/><button className="button primary small" onClick={send} data-testid="send-message">Send</button></div>
    </div>}
    {tab === "progress" && <div>
      {isProvider && user.id === booking.provider_user_id && <div className="progress-form">
        <label className="upload-box compact" data-testid="progress-upload-box"><Camera size={20}/><b>Add a site photo</b><small>JPG or PNG · up to 10MB</small><input type="file" accept="image/*" data-testid="progress-file" onChange={e => handleFile(e.target.files[0])}/></label>
        {pForm.image && <img src={pForm.image} alt="preview" className="progress-preview"/>}
        <label>Caption<input value={pForm.caption} onChange={e => setPForm({ ...pForm, caption: e.target.value })} placeholder="What was completed today?" data-testid="progress-caption"/></label>
        <button className="button primary" onClick={postProgress} disabled={busy} data-testid="post-progress">{busy ? "Posting…" : "Post update"} <ArrowUpRight size={16}/></button>
      </div>}
      <div className="progress-feed" data-testid="progress-feed">{progress.length === 0 ? <p className="muted small">No progress updates yet.{isProvider && user.id === booking.provider_user_id ? " Post the first one above." : ""}</p> : progress.map(p => <figure className="progress-item" key={p.id} data-testid={`progress-${p.id}`}><img src={p.image} alt={p.caption || "site photo"}/><figcaption><small className="muted">{chatTime(p.created_at)} · {p.provider_name}</small>{p.caption && <p>{p.caption}</p>}</figcaption></figure>)}</div>
    </div>}
  </Modal>;
}

export { ArrowUpRight, ChevronRight, FileText, Plus };
